import api from './api';
import {
  ApiResponse,
  Consultation,
  ConsultationCreate,
  ConsultationListItem,
  PaginatedResponse,
  Recording,
  FormData,
  ExtendedFormData,
} from '../models';

export interface ConsultationFilters {
  page?: number;
  page_size?: number;
  user_id?: number;
  date_from?: string;
  date_to?: string;
  status?: 'draft' | 'completed';
  search?: string;
}

export const consultationService = {
  async getList(filters?: ConsultationFilters): Promise<PaginatedResponse<ConsultationListItem>> {
    const response = await api.get<ApiResponse<PaginatedResponse<ConsultationListItem>>>(
      '/consultations/',
      { params: filters }
    );
    return response.data.data;
  },

  async getById(id: number): Promise<Consultation> {
    const response = await api.get<ApiResponse<Consultation>>(`/consultations/${id}/`);
    return response.data.data;
  },

  async create(data: ConsultationCreate): Promise<Consultation> {
    const response = await api.post<ApiResponse<Consultation>>('/consultations/', data);
    return response.data.data;
  },

  async update(id: number, data: Partial<Consultation>): Promise<Consultation> {
    const response = await api.patch<ApiResponse<Consultation>>(`/consultations/${id}/`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/consultations/${id}/`);
  },

  async updateFormData(id: number, formData: ExtendedFormData | Record<string, unknown>, templateId?: number | null): Promise<FormData> {
    const response = await api.post<ApiResponse<FormData>>(
      `/consultations/${id}/update_form_data/`,
      { data: formData, template_id: templateId }
    );
    return response.data.data;
  },

  async updateSummary(id: number, summary: string): Promise<{ summary: string }> {
    const response = await api.post<ApiResponse<{ summary: string }>>(
      `/consultations/${id}/update_summary/`,
      { summary }
    );
    return response.data.data;
  },

  async getAiAutofillStatus(
    id: number
  ): Promise<{
    llm_status: string | null;
    llm_result?: string;
    llm_parsed_result?: Record<string, unknown>;
    llm_error?: string;
    llm_processed_at?: string;
  }> {
    const response = await api.get<ApiResponse<{
      llm_status: string | null;
      llm_result?: string;
      llm_parsed_result?: Record<string, unknown>;
      llm_error?: string;
      llm_processed_at?: string;
    }>>(`/consultations/${id}/ai-autofill-status/`);
    return response.data.data;
  },

  // 상담에 LLM 프롬프트 적용 (source_recording 사용)
  async applyPrompt(
    id: number,
    options?: { prompt_id?: number; role?: string }
  ): Promise<{
    consultation_id: number;
    llm_task_id: string;
    llm_status: string;
    source_recording_id: number;
    prompt_id: number;
    prompt_name: string;
    role: string;
  }> {
    const response = await api.post<ApiResponse<{
      consultation_id: number;
      llm_task_id: string;
      llm_status: string;
      source_recording_id: number;
      prompt_id: number;
      prompt_name: string;
      role: string;
    }>>(`/consultations/${id}/apply-prompt/`, options || {});
    return response.data.data;
  },
};

export const recordingService = {
  async upload(consultationId: number, file: File): Promise<Recording> {
    const formData = new window.FormData();
    formData.append('consultation', consultationId.toString());
    formData.append('file', file);

    const response = await api.post<ApiResponse<Recording>>('/recordings/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  async getList(consultationId?: number): Promise<Recording[]> {
    const params = consultationId ? { consultation: consultationId } : {};
    const response = await api.get<ApiResponse<{ results: Recording[] }>>('/recordings/', { params });
    return response.data.data.results;
  },

  async getSTTStatus(id: number): Promise<{
    status: string;
    stt_text?: string;
    processed_at?: string;
    wedms_doc_id?: string;
    wedms_task_id?: string;
    task_status?: string;
    message?: string;
  }> {
    const response = await api.get<ApiResponse<{
      status: string;
      stt_text?: string;
      processed_at?: string;
      wedms_doc_id?: string;
      wedms_task_id?: string;
      task_status?: string;
      message?: string;
    }>>(`/recordings/${id}/stt-status/`);
    return response.data.data;
  },

  async getSTTResult(id: number): Promise<{
    stt_text: string;
    processed_at: string;
  }> {
    const response = await api.get<ApiResponse<{
      stt_text: string;
      processed_at: string;
    }>>(`/recordings/${id}/stt-result/`);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/recordings/${id}/`);
  },

  async getUnlinked(): Promise<Recording[]> {
    const response = await api.get<ApiResponse<Recording[]>>('/recordings/unlinked/');
    return response.data.data;
  },

  // STT 완료된 녹음 파일 목록 (AI 분석 가능한 것만)
  async getAvailableForAI(): Promise<Recording[]> {
    const response = await api.get<ApiResponse<Recording[]>>('/recordings/available-for-ai/');
    return response.data.data;
  },

  async linkToConsultation(recordingId: number, consultationId: number): Promise<Recording> {
    const response = await api.post<ApiResponse<Recording>>(
      `/recordings/${recordingId}/link-consultation/`,
      { consultation_id: consultationId }
    );
    return response.data.data;
  },

  async unlinkFromConsultation(recordingId: number): Promise<Recording> {
    const response = await api.post<ApiResponse<Recording>>(
      `/recordings/${recordingId}/unlink-consultation/`
    );
    return response.data.data;
  },

  async updateSttText(recordingId: number, sttText: string): Promise<Recording> {
    const response = await api.post<ApiResponse<Recording>>(
      `/recordings/${recordingId}/update-stt-text/`,
      { stt_text: sttText }
    );
    return response.data.data;
  },
};
